/**
 * IntegrityRow.jsx — WP7 Rev 4.4 integrity surface for one candidate.
 *
 * Two visible pieces:
 *   1. A status badge summarising detector state × human disposition.
 *      Findings are evidence for a human decision — never a verdict
 *      on the candidate.
 *   2. An expandable detail panel that loads the current-CHECK_VERSION
 *      check row + findings from
 *        GET /api/employer/v1/candidates/{id}/integrity
 *      and offers Include / Exclude / Change-disposition actions that
 *      POST to the WP5 endpoint (never the SECURITY DEFINER RPC).
 *
 * Cross-tenant safety: RLS filters the GET at the DB layer via
 * Migration 151 §165-190 policies. This component renders only
 * what the backend returned.
 *
 * Feb 2026 workflow correction:
 *   • All human-facing enum surfaces route through
 *     ./integrityFormatters so raw enums (review_required,
 *     inspection_exception, document_integrity_concern) NEVER
 *     appear in the DOM. The underlying API contract is unchanged.
 *   • Disposition reversibility is exposed through the same WP5
 *     endpoint / RPC authority: Awaiting→Include/Exclude,
 *     Included→Exclude, Excluded→Include.
 *   • D-3 Option C — reason enum stays authoritative; when reason is
 *     "other" a meaningful disposition_note becomes required. UI
 *     enforces this client-side and the server double-checks.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import {
  formatAggregateStatus,
  formatDisposition,
  formatDispositionReason,
  formatFailureCode,
  formatIndicator,
  formatConfidence,
  REASON_OPTIONS,
} from './integrityFormatters';


// Compact status descriptor. Prefers the authoritative backend
// `integrity_classification` (post-activation) over the raw
// `integrity_status` — the backend's WP6 classifier is the single
// source of truth for `grandfathered_legacy` vs `missing_check`,
// which the browser cannot compute.
function badgeFor(row) {
  const classification = row?.integrity_classification;
  const status = row?.integrity_status;
  const disp   = row?.integrity_disposition;

  if (classification === 'clear') {
    return {
      cls: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/5',
      Icon: ShieldCheck,
      label: 'Integrity: clear',
      title: 'Automated check found no hidden or adversarial text.',
    };
  }
  if (classification === 'included') {
    return {
      cls: 'border-amber-500/40 text-amber-200 bg-amber-500/5',
      Icon: ShieldAlert,
      label: 'Integrity: reviewed · included',
      title: 'Detector produced findings; a human included this dossier for evaluation.',
    };
  }
  if (classification === 'excluded') {
    return {
      cls: 'border-rose-500/40 text-rose-200 bg-rose-500/5',
      Icon: ShieldAlert,
      label: 'Integrity: reviewed · excluded',
      title: 'Detector produced findings; a human excluded this dossier from evaluation.',
    };
  }
  if (classification === 'unresolved') {
    return {
      cls: 'border-amber-500/60 text-amber-100 bg-amber-500/10',
      Icon: ShieldAlert,
      label: status === 'unable_to_verify'
        ? 'Integrity: unverifiable · needs decision'
        : 'Integrity: needs decision',
      title: 'A human decision (include or exclude) is required before evaluation.',
    };
  }
  if (classification === 'grandfathered_legacy') {
    return {
      cls: 'border-slate-500/40 text-slate-200 bg-slate-500/5',
      Icon: ShieldQuestion,
      label: 'Not checked (uploaded before this feature)',
      title: 'This candidate predates the integrity control. Eligible for evaluation.',
    };
  }
  if (classification === 'missing_check') {
    return {
      cls: 'border-orange-500/60 text-orange-200 bg-orange-500/10',
      Icon: ShieldAlert,
      label: 'Integrity check unavailable',
      title: 'The required integrity check is missing. Evaluation is blocked until the check is repaired.',
    };
  }

  // Pre-activation fallback — backend has no classification yet.
  if (status === 'clear') {
    return {
      cls: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/5',
      Icon: ShieldCheck,
      label: 'Integrity: clear',
      title: 'Automated check found no hidden or adversarial text.',
    };
  }
  if (status === 'review_required' || status === 'unable_to_verify') {
    if (disp === 'included_for_evaluation') {
      return {
        cls: 'border-amber-500/40 text-amber-200 bg-amber-500/5',
        Icon: ShieldAlert,
        label: 'Integrity: reviewed · included',
        title: 'Detector produced findings; a human included this dossier for evaluation.',
      };
    }
    if (disp === 'excluded_from_evaluation') {
      return {
        cls: 'border-rose-500/40 text-rose-200 bg-rose-500/5',
        Icon: ShieldAlert,
        label: 'Integrity: reviewed · excluded',
        title: 'Detector produced findings; a human excluded this dossier from evaluation.',
      };
    }
    return {
      cls: 'border-amber-500/60 text-amber-100 bg-amber-500/10',
      Icon: ShieldAlert,
      label: status === 'unable_to_verify' ? 'Integrity: unverifiable' : 'Integrity: needs decision',
      title: 'A human decision (include or exclude) is required before evaluation.',
    };
  }
  return {
    cls: 'border-slate-500/40 text-slate-200 bg-slate-500/5',
    Icon: ShieldQuestion,
    label: 'Integrity: not checked',
    title: 'No integrity check on file for this candidate.',
  };
}


const _DISPOSE_ROLES = new Set(['owner', 'admin', 'recruiter']);


function FindingLine({ f }) {
  const label = formatIndicator(f.indicator_type);
  const conf  = formatConfidence(f.confidence);
  const bits = [];
  if (f.page_number != null)     bits.push(`p.${f.page_number}`);
  if (f.paragraph_index != null) bits.push(`¶${f.paragraph_index}`);
  if (f.font_size != null)       bits.push(`font ${f.font_size}pt`);
  if (f.font_color_hex)          bits.push(`text colour #${f.font_color_hex}`);
  if (f.bg_color_hex)            bits.push(`background #${f.bg_color_hex}`);
  if (f.char_count != null)      bits.push(`${f.char_count} chars`);

  return (
    <li className="py-2 first:pt-0 border-b border-ndor-edge/40 last:border-b-0"
        data-testid="integrity-finding-row">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ndor-ink">
            <span className="font-medium">{label}</span>
            <span className="ml-2 text-xs uppercase tracking-wider text-slate-400"
                  data-testid="integrity-finding-confidence">
              {conf}
            </span>
          </p>
          {bits.length > 0 && (
            <p className="text-xs text-slate-400 mt-0.5">{bits.join(' · ')}</p>
          )}
          {f.text_fragment && (
            <p className="text-xs italic text-slate-300 mt-1 line-clamp-2"
               title={f.text_fragment}>
              &ldquo;{f.text_fragment}&rdquo;
            </p>
          )}
        </div>
      </div>
    </li>
  );
}


// ─── DisposeDialog — reason + note, Option C client-side ────────


function DisposeDialog({
  open, mode, currentDisposition, busy, onCancel, onSubmit,
}) {
  // `mode` ∈ {'include', 'exclude'}. `currentDisposition` may be null
  // (Awaiting) or one of the two disposition tokens (reversal).
  const [reason, setReason]         = useState('document_integrity_concern');
  const [note,   setNote]           = useState('');
  useEffect(() => {
    if (open) {
      setReason('document_integrity_concern');
      setNote('');
    }
  }, [open, mode]);

  if (!open) return null;

  const targetDisposition = mode === 'include'
    ? 'included_for_evaluation'
    : 'excluded_from_evaluation';

  // Exclude → reason required (server-enforced). Include → reason
  // optional (may be null). Option C: reason='other' → note required.
  const reasonRequired = mode === 'exclude';
  const submittedReason = reasonRequired ? reason : (reason || null);
  const noteRequired    = reason === 'other';
  const noteTrimmed     = note.trim();
  const canSubmit = Boolean(
    !busy
    && (mode === 'include' || reason)
    && (!noteRequired || noteTrimmed.length > 0),
  );

  const title = mode === 'include'
    ? (currentDisposition === 'excluded_from_evaluation'
        ? 'Reverse: include for evaluation'
        : 'Include for evaluation')
    : (currentDisposition === 'included_for_evaluation'
        ? 'Reverse: exclude from evaluation'
        : 'Exclude from evaluation');

  return (
    <div
      className="mt-4 rounded-md border border-ndor-edge bg-ndor-surface/60 p-4 space-y-3"
      data-testid="integrity-dispose-dialog"
      data-mode={mode}
    >
      <p className="text-sm font-medium text-ndor-ink">{title}</p>
      <p className="text-xs text-slate-300">
        {mode === 'include'
          ? 'The candidate will become eligible for evaluation. Reason is optional; a note helps future reviewers understand the decision.'
          : 'The candidate will be excluded from evaluation. A reason is required.'}
      </p>

      <div className="space-y-1.5">
        <label
          htmlFor="integrity-dispose-reason"
          className="text-[11px] uppercase tracking-wider text-slate-300"
        >
          Reason {reasonRequired ? <span className="text-rose-300">(required)</span> : <span className="text-slate-500">(optional)</span>}
        </label>
        <select
          id="integrity-dispose-reason"
          data-testid="integrity-dispose-reason-select"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-md border border-ndor-edge bg-ndor-bg px-2.5 py-1.5 text-sm text-ndor-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          {!reasonRequired && <option value="">— select a reason —</option>}
          {REASON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="integrity-dispose-note"
          className="text-[11px] uppercase tracking-wider text-slate-300"
        >
          Note{' '}
          {noteRequired
            ? <span className="text-rose-300">(required when reason is &ldquo;other&rdquo;)</span>
            : <span className="text-slate-500">(optional, up to 2000 characters)</span>}
        </label>
        <Textarea
          id="integrity-dispose-note"
          data-testid="integrity-dispose-note"
          rows={3}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={noteRequired
            ? 'Please describe the reason in your own words.'
            : 'Optional context to help future reviewers.'}
          className="w-full bg-ndor-bg border-ndor-edge text-sm text-ndor-ink"
        />
        {noteRequired && noteTrimmed.length === 0 && (
          <p className="text-[11px] text-rose-300">
            A written explanation is required when the reason is &ldquo;other&rdquo;.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs uppercase tracking-wider border-ndor-edge text-slate-200"
          onClick={onCancel}
          disabled={busy}
          data-testid="integrity-dispose-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`h-8 text-xs uppercase tracking-wider ${
            mode === 'include'
              ? 'border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10'
              : 'border-rose-500/40 text-rose-200 hover:bg-rose-500/10'
          }`}
          disabled={!canSubmit}
          onClick={() => onSubmit({
            disposition:      targetDisposition,
            reason:           submittedReason,
            disposition_note: noteTrimmed.length > 0 ? noteTrimmed : null,
          })}
          data-testid="integrity-dispose-confirm"
        >
          {busy
            ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Working…</>
            : (mode === 'include' ? 'Confirm include' : 'Confirm exclude')}
        </Button>
      </div>
    </div>
  );
}


export default function IntegrityRow({ candidate, memberRole, onDisposed }) {
  const [expanded, setExpanded] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [detail,   setDetail]   = useState(null);
  const [busy,     setBusy]     = useState(false);
  const [dialog,   setDialog]   = useState(null); // 'include' | 'exclude' | null

  const badge = badgeFor(candidate);
  const hasDisposeAuthority = Boolean(memberRole && _DISPOSE_ROLES.has(memberRole));

  const classification = candidate?.integrity_classification;
  const currentDisposition = candidate?.integrity_disposition
    || detail?.check?.disposition
    || null;

  // Reversibility surface: which actions are available for THIS candidate.
  //   Awaiting (unresolved) → Include or Exclude
  //   Included               → Exclude
  //   Excluded               → Include
  //   Clear / others         → nothing (evaluation runs on Eligible view)
  const canInclude = hasDisposeAuthority && Boolean(candidate?.integrity_check_id) && (
    classification === 'unresolved' || classification === 'excluded'
  );
  const canExclude = hasDisposeAuthority && Boolean(candidate?.integrity_check_id) && (
    classification === 'unresolved' || classification === 'included' || classification === 'clear'
  );

  const loadDetail = useCallback(async () => {
    if (!candidate?.id) return;
    setLoading(true);
    try {
      const body = await apiFetch(
        `/api/employer/v1/candidates/${candidate.id}/integrity`,
      );
      setDetail(body || null);
    } catch (err) {
      setDetail(null);
      const code = err?.code || err?.data?.detail?.code || 'INTEGRITY_LOAD_FAILED';
      toast.error(`${code}: ${err?.message || 'Failed to load integrity detail.'}`);
    } finally {
      setLoading(false);
    }
  }, [candidate?.id]);

  useEffect(() => {
    if (expanded && !detail) {
      loadDetail();
    }
  }, [expanded, detail, loadDetail]);

  const doDispose = useCallback(async (payload) => {
    setBusy(true);
    try {
      await apiFetch(
        `/api/employer/v1/candidates/${candidate.id}/integrity/dispose`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );
      toast.success(
        payload.disposition === 'included_for_evaluation'
          ? 'Included for evaluation.'
          : 'Excluded from evaluation.',
      );
      setDialog(null);
      setDetail(null);
      if (typeof onDisposed === 'function') {
        onDisposed(candidate.id, payload.disposition);
      }
    } catch (err) {
      const code = err?.code || err?.data?.detail?.code || 'DISPOSE_FAILED';
      const msg  = err?.data?.detail?.message || err?.message || 'Disposition failed.';
      toast.error(`${code}: ${msg}`);
    } finally {
      setBusy(false);
    }
  }, [candidate.id, onDisposed]);

  const summaryLines = useMemo(() => {
    if (!detail?.check) return [];
    const check = detail.check;
    const lines = [
      { key: 'machine',
        label: 'Machine result',
        value: formatAggregateStatus(check.aggregate_status),
        testid: 'integrity-line-aggregate' },
    ];
    if (check.integrity_failure_code) {
      lines.push({
        key: 'failure',
        label: 'Inspection detail',
        value: formatFailureCode(check.integrity_failure_code),
        testid: 'integrity-line-failure',
      });
    }
    if (check.disposition) {
      lines.push({
        key: 'disposition',
        label: 'Employer decision',
        value: formatDisposition(check.disposition),
        testid: 'integrity-line-disposition',
      });
    }
    if (check.disposition_reason) {
      lines.push({
        key: 'reason',
        label: 'Reason',
        value: formatDispositionReason(check.disposition_reason),
        testid: 'integrity-line-reason',
      });
    }
    if (check.disposition_note) {
      lines.push({
        key: 'note',
        label: 'Note',
        value: check.disposition_note,
        testid: 'integrity-line-note',
      });
    }
    return lines;
  }, [detail]);

  const Icon = badge.Icon;

  return (
    <div className="mt-2" data-testid="integrity-row" data-candidate-id={candidate.id}>
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
        data-testid="integrity-badge-toggle"
        data-expanded={expanded ? 'true' : 'false'}
        title={badge.title}
      >
        <Badge
          variant="outline"
          className={`${badge.cls} text-[10px] uppercase tracking-wider flex items-center gap-1`}
          data-testid="integrity-badge"
          data-status={candidate.integrity_status || 'not_checked'}
          data-disposition={candidate.integrity_disposition || 'none'}
          data-classification={classification || 'none'}
        >
          <Icon className="h-3 w-3" />
          {badge.label}
        </Badge>
        {expanded
          ? <ChevronDown className="h-3 w-3 text-slate-400" />
          : <ChevronRight className="h-3 w-3 text-slate-400" />}
      </button>

      {expanded && (
        <div
          className="mt-3 rounded-md border border-ndor-edge bg-ndor-bg/40 p-4"
          data-testid="integrity-detail-panel"
        >
          {loading && (
            <p className="text-xs text-slate-400 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading integrity detail…
            </p>
          )}

          {!loading && detail && detail.check === null && (
            <p className="text-xs text-slate-300"
               data-testid="integrity-no-check">
              {classification === 'missing_check'
                ? 'The required integrity check is missing for this candidate. This is not evidence against the candidate — it indicates the automated check did not persist. Evaluation is blocked until the integrity state is repaired (re-ingest or ops remediation).'
                : 'No integrity check on file for this candidate. Older uploads predate this control.'}
            </p>
          )}

          {!loading && detail && detail.check && (
            <>
              <div className="text-xs text-slate-300 space-y-1"
                   data-testid="integrity-check-summary">
                {summaryLines.map((line) => (
                  <p key={line.key} data-testid={line.testid}>
                    {line.label}:{' '}
                    <span className="text-ndor-ink font-medium">{line.value}</span>
                  </p>
                ))}
              </div>

              {detail.findings && detail.findings.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                    Findings ({detail.findings.length})
                  </p>
                  <ul className="max-h-64 overflow-y-auto"
                      data-testid="integrity-findings-list">
                    {detail.findings.map((f) => (
                      <FindingLine key={f.id} f={f} />
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-slate-500">
                    Findings describe observed document properties. They are
                    evidence for a human decision — not a verdict on the
                    candidate.
                  </p>
                </div>
              )}

              {hasDisposeAuthority && !dialog && (canInclude || canExclude) && (
                <div className="mt-4 flex flex-wrap gap-2"
                     data-testid="integrity-actions">
                  {canInclude && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs uppercase tracking-wider border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                      onClick={() => setDialog('include')}
                      disabled={busy}
                      data-testid="integrity-include-btn"
                      data-reversal={currentDisposition === 'excluded_from_evaluation' ? 'true' : 'false'}
                    >
                      {currentDisposition === 'excluded_from_evaluation'
                        ? 'Reverse to include'
                        : 'Include for evaluation'}
                    </Button>
                  )}
                  {canExclude && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs uppercase tracking-wider border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
                      onClick={() => setDialog('exclude')}
                      disabled={busy}
                      data-testid="integrity-exclude-btn"
                      data-reversal={currentDisposition === 'included_for_evaluation' ? 'true' : 'false'}
                    >
                      {currentDisposition === 'included_for_evaluation'
                        ? 'Reverse to exclude'
                        : 'Exclude from evaluation'}
                    </Button>
                  )}
                </div>
              )}

              <DisposeDialog
                open={dialog !== null}
                mode={dialog}
                currentDisposition={currentDisposition}
                busy={busy}
                onCancel={() => setDialog(null)}
                onSubmit={doDispose}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
